<!--
  @component StorageCreateWizard — Phase 3 "New shared store" modal.
  Create a standalone shared asset store (not tied to any project), then link
  (share) it to any number of the owner's projects — the M:N create flow.
  reads:  useDeploymentIO (storage.shared.create + storage.shared.attach),
          useIdentityIO (projects)
  steps:  Name → Share → Review
-->
<script lang="ts">
  import Wizard, { type WizardStepDef } from "../../../../global/visuals/primitives/Wizard.svelte";
  import { useIdentityIO } from "../../accounts/IdentityIO.svelte";
  import { useDeploymentIO } from "../../../../global/io/nodes/deployment/DeploymentIO.svelte";

  interface Props {
    open: boolean;
    onClose: () => void;
    onCreated?: () => void;
  }
  const { open, onClose, onCreated }: Props = $props();

  const identity = useIdentityIO();
  const node = useDeploymentIO();

  const STEPS: readonly WizardStepDef[] = [
    { id: "name", label: "Name" },
    { id: "share", label: "Share" },
    { id: "review", label: "Review" },
  ];

  let step = $state(0);
  let name = $state("");
  let shareIds = $state<string[]>([]);
  let pending = $state(false);
  let error = $state<string | null>(null);
  let phase = $state<string>("");

  const projects = $derived(identity.projects);

  $effect(() => {
    if (open) { step = 0; name = ""; shareIds = []; pending = false; error = null; phase = ""; }
  });

  const NAME_RE = /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/;
  const trimmed = $derived(name.trim().toLowerCase());
  const nameError = $derived.by<string | null>(() => {
    if (!trimmed) return null;
    if (!NAME_RE.test(trimmed)) return "lowercase a–z, 0–9, and dashes (no leading/trailing)";
    return null;
  });

  function nameOf(id: string): string { return projects.find((p) => p.id === id)?.name ?? id; }
  function toggleShare(id: string): void {
    shareIds = shareIds.includes(id) ? shareIds.filter((x) => x !== id) : [...shareIds, id];
  }

  const canAdvance = $derived.by(() => {
    if (pending) return false;
    if (step === 0) return trimmed.length > 0 && nameError === null;
    return true;
  });

  async function finish(): Promise<void> {
    if (!canAdvance || pending || !trimmed) return;
    pending = true; error = null;
    try {
      phase = "Creating shared store…";
      const r = (await node.dispatch("storage.shared.create", { name: trimmed })) as { storeId: string };
      const storeId = r.storeId;
      for (const pid of shareIds) {
        phase = `Linking ${nameOf(pid)}…`;
        await node.dispatch("storage.shared.attach", { target: { id: pid }, storeId });
      }
      onCreated?.();
      onClose();
    } catch (e) {
      error = e instanceof Error ? e.message : "could not create shared store";
    } finally {
      pending = false; phase = "";
    }
  }
</script>

<Wizard
  {open}
  title="New shared store"
  steps={STEPS}
  currentStep={step}
  {canAdvance}
  {pending}
  finishLabel="Create store"
  onStepChange={(n) => (step = n)}
  onFinish={() => void finish()}
  onCancel={onClose}
>
  {#snippet children(stepIndex: number)}
    {#if stepIndex === 0}
      <div class="step">
        <label>
          <span>store name</span>
          <input type="text" placeholder="brand-assets" autocomplete="off" spellcheck="false" bind:value={name} disabled={pending} maxlength={40} />
        </label>
        <p class="hint" class:bad={!!nameError}>
          {nameError ?? "A shared asset library, not tied to one project. Lowercase letters, numbers, and dashes."}
        </p>
      </div>
    {:else if stepIndex === 1}
      <div class="step">
        <p class="lbl">Share with projects</p>
        <p class="hint">Each linked project's app gets the store's location injected on its next deploy. You can change links anytime in the matrix.</p>
        <div class="list">
          {#each projects as p (p.id)}
            <button type="button" class="opt" class:sel={shareIds.includes(p.id)} disabled={pending} onclick={() => toggleShare(p.id)}>
              <span class="check" class:on={shareIds.includes(p.id)}></span>
              <span class="opt-name">{p.name}</span>
            </button>
          {/each}
          {#if projects.length === 0}<p class="hint">No projects yet — you can still create the store and link projects later.</p>{/if}
        </div>
      </div>
    {:else}
      <div class="step review">
        <dl>
          <div class="row"><dt>name</dt><dd><code>{trimmed}</code></dd></div>
          <div class="row"><dt>kind</dt><dd>shared asset store</dd></div>
          <div class="row"><dt>shared with</dt><dd>{shareIds.length ? shareIds.map(nameOf).join(", ") : "— (none yet)"}</dd></div>
        </dl>
        <p class="note"><strong>Next:</strong> we allocate the shared namespace, then link it to each selected project so its app can reach the same assets.</p>
        {#if pending && phase}<p class="phase">{phase}</p>{/if}
        {#if error}<p class="error">{error}</p>{/if}
      </div>
    {/if}
  {/snippet}
</Wizard>

<style>
  .step { display: flex; flex-direction: column; gap: var(--space-2); }
  label { display: flex; flex-direction: column; gap: var(--space-1, 4px); }
  label span { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-tertiary); }
  input { padding: var(--space-2) var(--space-3); border-radius: var(--radius-sm); border: 0.5px solid var(--border-input); background: var(--surface-sunken); color: var(--text-primary); font: inherit; font-size: 13px; font-family: var(--type-mono); }
  input:focus { outline: none; border-color: var(--border-focus); }
  .lbl { margin: 0; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-tertiary); }
  .hint { margin: 0 0 var(--space-1); font-size: 11.5px; color: var(--text-tertiary); line-height: 1.5; }
  .hint.bad { color: var(--error); font-family: var(--type-mono); }
  .list { display: flex; flex-direction: column; gap: 2px; max-height: 280px; overflow-y: auto; scrollbar-width: thin; }
  .opt { display: flex; align-items: center; gap: var(--space-2); background: transparent; border: 0.5px solid transparent; border-radius: var(--radius-md); padding: 9px var(--space-2); cursor: pointer; text-align: left; font: inherit; width: 100%; transition: background var(--ease-fast), border-color var(--ease-fast); }
  .opt:hover:not(:disabled) { background: var(--bg-hover); }
  .opt.sel { background: var(--bg-selected); border-color: var(--border-focus); }
  .opt:disabled { cursor: not-allowed; opacity: 0.6; }
  .opt-name { flex: 1; min-width: 0; font-size: 13.5px; color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .check { width: 15px; height: 15px; flex-shrink: 0; border: 1px solid var(--border-strong); background: var(--surface-sunken); border-radius: 4px; }
  .check.on { border-color: var(--accent, #5b8cff); background: var(--accent, #5b8cff); }
  .review dl { display: flex; flex-direction: column; gap: var(--space-2); margin: 0; }
  .row { display: flex; gap: var(--space-3); align-items: baseline; }
  dt { flex: 0 0 110px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: var(--text-tertiary); }
  dd { margin: 0; font-size: 12.5px; color: var(--text-primary); }
  dd code { font-family: var(--type-mono); font-size: 12px; }
  .note { margin: var(--space-2) 0 0; padding: var(--space-3); background: var(--warning-subtle); border-radius: var(--radius-sm); font-size: 12px; color: var(--text-secondary); line-height: 1.55; }
  .note strong { color: var(--warning); font-weight: 500; }
  .phase { margin: var(--space-2) 0 0; font-size: 12.5px; color: var(--text-secondary); }
  .error { margin: var(--space-2) 0 0; font-size: 12px; color: var(--error); font-family: var(--type-mono); }
</style>
